'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import type { CategoriesEntity } from '@eliadmin/core/src/api/general/types/general-layout-types';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@eliadmin/core/src/components/ui/accordion';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Separator } from '@eliadmin/core/src/components/ui/separator';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTrigger,
} from '@eliadmin/core/src/components/ui/sheet';
import { HEADER_CONFIG } from '@eliadmin/core/src/constants/header-config';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { getSafeHref, isRelativeHref } from '@eliadmin/core/src/utils/url-utils';
import Link from '@eliadmin/core/src/components/link';
import { LanguageSelect } from './language-select';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import { LiaArrowRightSolid } from 'react-icons/lia';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { useQuery } from '@tanstack/react-query';
import { fetchCategories } from '@eliadmin/core/src/api/products/product-api';
import { buildWhereParams, storeApiResources } from '@eliadmin/core/src/api/store-list-request';
import { ROUTES } from '@eliadmin/core/src/constants/routes';

export type MobileSidebarProps = {
  className?: {
    trigger?: string;
    content?: string;
    firstSeparator?: string;
    menuList?: string;
    menuLink?: string;
    menuGroup?: string;
    menuSeparator?: string;
    buttonWrapper?: string;
    button?: string;
    iconsWrapper?: string;
    icon?: string;
  };
  side?: 'right' | 'left' | 'bottom' | 'top';
  firstSeparator?: boolean;
  menu?: {
    type: 'page';
    name: string;
    path?: string;
  }[];
  menuArrow?: boolean;
  menuSeparator?: boolean;
  categories?: CategoriesEntity[];
  locale: string;
};

export const MobileSidebar = (props: MobileSidebarProps) => {
  const locale = props.locale;
  const generalLayout = useGlobalStore(state => state.generalLayout);
  const { t } = useTranslations();

  const { data: categories = { data: [] } } = useQuery({
    queryKey: [CacheKey.CATEGORIES, locale],
    queryFn: () =>
      fetchCategories({
        locale,
        query: {
          childs: true,
          image: true,
          ...buildWhereParams({ parent: { equals: 1 } }, storeApiResources.storeCategories),
        },
      }),
  });

  const categoriesByUsage = categories.data.filter(category =>
    category.short_description.includes(locale === 'sk' ? 'Podľa použitia' : 'According to use'),
  );

  const categoriesByMaterial = categories.data.filter(category =>
    category.short_description.includes(
      locale === 'sk' ? 'Podľa materi&aacute;lu' : 'According to the material',
    ),
  );

  return (
    <>
      <Sheet>
        <SheetTrigger aria-label="Menu"
          className={cn('text-secondary', 'ea-mobile-sidebar__trigger', props.className?.trigger)}
        >
          {HEADER_CONFIG?.sidebar?.triggerIcon}
        </SheetTrigger>
        <SheetContent
          side={'right'}
          className={cn(
            'flex w-screen flex-col items-start justify-start gap-8 overflow-scroll border-none p-4 pt-16 shadow-none sm:w-[420px]',
            'ea-mobile-sidebar__content',
            'hide-scroll-bar',
          )}
          closeIcon={{
            wrapper: 'right-4 top-4',
            icon: 'text-primary h-6 w-6 font-bold',
          }}
        >
          {!!props.menu && (
            <div className="flex w-full flex-col gap-2">
              {props.menu?.map((item, index) => {
                const Comp = item.path
                  ? isRelativeHref(getSafeHref(item.path))
                    ? Link
                    : 'a'
                  : 'span';
                return (
                  <>
                    {index === 2 ? (
                      <Accordion type="single" collapsible className="w-full">
                        <AccordionItem
                          value={item.name}
                          className={cn('w-full', 'ea-mobile-sidebar__accordion-item')}
                        >
                          <AccordionTrigger
                            className={cn(
                              'ea-mobile-sidebar__accordion-trigger',
                              'w-full px-3 py-2 font-heading text-base font-normal uppercase text-foreground',
                            )}
                          >
                            {item.name}
                          </AccordionTrigger>
                          <AccordionContent className={cn('ea-mobile-sidebar__accordion-content')}>
                            <div className="flex w-full flex-col items-start justify-start gap-2">
                              <p className="font-heading font-medium uppercase text-[#8E8F92]">
                                {t('text1245')}
                              </p>
                              {categoriesByUsage.map(category => (
                                <SheetClose asChild key={category.slug}>
                                  <Link prefetchOnHover
                                    prefetch={false}
                                    href={ROUTES.CATEGORY({ slug: category.slug, page: 1 })}
                                    className={cn(
                                      'group transition-all',
                                      'flex w-full items-center justify-between py-2',
                                    )}
                                  >
                                    <div className="flex items-center justify-start gap-3">
                                      {category?.icon?.url && (
                                        <Image
                                          src={category.icon.url}
                                          alt={resolveImageAlt(category?.icon, category?.name)}
                                          width={0}
                                          height={0}
                                          className={cn(
                                            'h-6 w-6 lg:h-8 lg:w-8',
                                            'ea-categories-list__icon',
                                            'svg-icon',
                                          )}
                                        />
                                      )}
                                      <h5
                                        className={cn(
                                          'font-heading uppercase',
                                          'text-lg font-normal text-foreground transition sm:text-xl lg:text-2xl',
                                        )}
                                      >
                                        {category.name}
                                      </h5>
                                    </div>
                                    <LiaArrowRightSolid className="h-6 w-6 text-[#F7F7F7] lg:h-8 lg:w-8" />
                                  </Link>
                                </SheetClose>
                              ))}
                            </div>
                            <div className="flex w-full flex-col items-start justify-start gap-2">
                              <p className="font-heading font-medium uppercase text-[#8E8F92]">
                                {t('text1246')}
                              </p>
                              {categoriesByMaterial.map(category => (
                                <SheetClose key={category.slug} asChild>
                                  <Link prefetchOnHover
                                    prefetch={false}
                                    href={ROUTES.CATEGORY({ slug: category.slug, page: 1 })}
                                    className={cn(
                                      'group transition-all',
                                      'flex w-full items-center justify-between py-2',
                                    )}
                                  >
                                    <div className="flex items-center justify-start gap-3">
                                      {category?.icon?.url && (
                                        <Image
                                          src={category.icon.url}
                                          alt={resolveImageAlt(category?.icon, category?.name)}
                                          width={0}
                                          height={0}
                                          className={cn(
                                            'h-6 w-6 lg:h-8 lg:w-8',
                                            'ea-categories-list__icon',
                                            'svg-icon',
                                          )}
                                        />
                                      )}
                                      <h5
                                        className={cn(
                                          'font-heading uppercase',
                                          'text-lg font-normal text-foreground transition sm:text-xl lg:text-2xl',
                                        )}
                                      >
                                        {category.name}
                                      </h5>
                                    </div>
                                    <LiaArrowRightSolid className="h-6 w-6 text-[#F7F7F7] lg:h-8 lg:w-8" />
                                  </Link>
                                </SheetClose>
                              ))}
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      </Accordion>
                    ) : (
                      <SheetClose key={index} asChild>
                        <Comp
                          href={item.path ? getSafeHref(item.path) : (undefined as any)}
                          className="flex w-full px-3 py-2"
                          prefetch={false}
                        >
                          <div
                            className={cn(
                              'font-heading text-base font-normal uppercase text-foreground',
                              '',
                            )}
                          >
                            {item.name}
                          </div>
                        </Comp>
                      </SheetClose>
                    )}
                  </>
                );
              })}
            </div>
          )}
          <Separator orientation="horizontal" className="bg-[#E5E5E5]" />
          <SheetClose asChild>
            <Button asChild className="w-full font-heading font-medium uppercase">
              <Link prefetchOnHover prefetch={false} href={ROUTES.CONTACT()}>
                {t('text1256')}
              </Link>
            </Button>
          </SheetClose>
          <Separator orientation="horizontal" className="bg-[#E5E5E5]" />
          <LanguageSelect />
        </SheetContent>
      </Sheet>
    </>
  );
};

export default MobileSidebar;
